/**
 * NativeHandshake - owns the native-bridge handshake end to end.
 *
 * Split out of `BifrostBridge` because the handshake's invariants are about ordering,
 * lifetime and re-entrancy, and they only hold if they are enforced in one place.
 * Interleaved with RPC routing they had no locality: a caller could reach the state
 * that host code was concurrently mutating, and two owners scheduled evaluation.
 *
 * Everything about how a handshake progresses is hidden here -- which shim a send and
 * an ack belong to, how host re-entrancy is serialized, how the deadline and the
 * shared-watch memo interact, and when evaluation is scheduled. The RPC side only
 * awaits an outcome, forwards `native_ready`, and stops it on teardown.
 */

import { DEFAULT_READY_TIMEOUT_MS } from "../types/protocol";
// BEGIN stforestkit#469 compat — delete with `src/compat` (see its README).
import { attemptStForestKitOnceGuardRecovery } from "../compat/StForestKitOnceGuardRecovery";
// END stforestkit#469 compat

type NativeBridge = NonNullable<typeof window.bifrostNativeBridge>;

/**
 * One shared attempt at the native-bridge handshake.
 *
 * A handshake is a property of a single shim object, not of the bridge and not of
 * `window`: we sent `onWebReady()` to *that* object and *that* object reported
 * ready. Both halves are therefore tracked against `generation`, the shim they were
 * observed on, and reset together when the host swaps in a different one. Tracking
 * them independently lets a replaced shim contribute one half of a handshake that
 * no single shim ever completed — the caller then resolves and talks to a bridge
 * that is not listening, which is indistinguishable downstream from a healthy one.
 */
interface BridgeWatch {
  /** Wall-clock time after which polling gives up. */
  deadline: number;
  stopped: boolean;
  /** Stop polling and release the shared-watch memo. */
  stop: () => void;
  /** Resolves once one shim has both been sent `onWebReady()` and reported ready. */
  settled: Promise<void>;
  /** The shim the current attempt is bound to; `undefined` until one is seen. */
  generation?: NativeBridge;
  /** Whether `generation` has been sent this watch's `onWebReady()`. */
  sent: boolean;
  /** Whether `generation` acked with `native_ready` after that send. */
  acked: boolean;
  /**
   * Identifies the `onWebReady()` call currently outstanding, so an ack can be
   * attributed to the send it answers.
   *
   * A bare `"native_ready"` names no sender, so once a replacement shim has been
   * sent to, a straggling ack from its predecessor is indistinguishable from the
   * replacement's own. Shim identity cannot separate them either: by the time the
   * straggler arrives the re-arm has already run, so `generation` is the new shim
   * and `sent` is true.
   */
  handshakeId: string;
  /** True only while this watch's `shim.onWebReady()` call is on the stack. */
  sending: boolean;
  /** Record a `native_ready` ack; ignored unless it answers this watch's own send. */
  signalNativeReady: (ack: NativeReadyAck) => void;
  /**
   * Re-run the handshake now and settle if it completed. Set once polling starts,
   * so an ack does not have to wait for the next poll interval.
   *
   * Evaluation is serialized: calling this from inside an evaluation records the
   * need for another pass rather than nesting. Every host-code seam --
   * `onWebReady()`, the `isReady` getter, and the ack path -- can re-enter here, so
   * guarding them one at a time has repeatedly missed one.
   */
  evaluate?: () => void;
  /** True while an evaluation is on the stack. */
  evaluating: boolean;
  /** Something arrived mid-evaluation; the current pass drains it before returning. */
  rerunRequested: boolean;
}

/**
 * A `native_ready` message. `bare` distinguishes the legacy string, which names no
 * send, from the envelope form, which does.
 */
interface NativeReadyAck {
  bare: boolean;
  handshakeId?: unknown;
}

/** What the handshake needs from its host bridge, and nothing more. */
export interface HandshakeHost {
  /** Terminal for every in-flight attempt; the bridge owns this flag. */
  isDestroyed: () => boolean;
  /** A handshake completed. Fired once per completing watch. */
  onCompleted: () => void;
}

export class NativeHandshake {
  private static handshakeCounter = 0;

  /** Monotonic so ids never collide within a document; not security-relevant. */
  private static nextHandshakeId(): string {
    NativeHandshake.handshakeCounter += 1;
    return `hs-${NativeHandshake.handshakeCounter}`;
  }

  /**
   * Upper bound on passes drained in one synchronous evaluation. Reached only by a
   * host that keeps asking for another pass without ever completing the handshake.
   */
  private static readonly MAX_EVALUATION_PASSES = 64;

  /** Interval between native-bridge presence checks. */
  private static readonly BRIDGE_POLL_INTERVAL_MS = 100;

  private pendingWatch?: BridgeWatch;

  constructor(private readonly host: HandshakeHost) {}

  /**
   * Resolve when the handshake completes, reject at `timeoutMs`.
   *
   * Overlapping callers share one attempt, so the shim is sent one `onWebReady()`
   * between them rather than one each.
   */
  async await(timeoutMs: number = DEFAULT_READY_TIMEOUT_MS): Promise<void> {
    const watch = this.bridgeWatch(timeoutMs);

    let timeoutTimer: ReturnType<typeof setTimeout> | undefined;
    const timeoutPromise = new Promise<void>((_, reject) => {
      timeoutTimer = setTimeout(
        () => reject(new Error("Native bridge ready timeout")),
        timeoutMs,
      );
    });

    try {
      // No `watch.stop()` on success: `bridgeWatch` registers its own retirement on
      // `settled` first, so it has always run by the time this resumes.
      await Promise.race([watch.settled, timeoutPromise]);
    } finally {
      clearTimeout(timeoutTimer);
    }
  }

  /** Route an inbound `native_ready` to the attempt in flight, if any. */
  handleNativeReady(message: "native_ready" | { handshakeId?: unknown }): void {
    this.pendingWatch?.signalNativeReady(
      typeof message === "string"
        ? { bare: true }
        : { bare: false, handshakeId: message.handshakeId },
    );
  }

  /**
   * Either the legacy bare string, or an envelope echoing back the id we passed to
   * `onWebReady()`. The echoing form is what lets an asynchronous ack be attributed
   * to the send it answers; a bare one only counts synchronously.
   */
  isNativeReady(
    message: any,
  ): message is
    | "native_ready"
    | { type: "native_ready"; handshakeId?: unknown } {
    return (
      message === "native_ready" ||
      (typeof message === "object" &&
        message !== null &&
        message.type === "native_ready")
    );
  }

  /** Abandon any attempt in flight. */
  stop(): void {
    this.pendingWatch?.stop();
  }

  /**
   * Advance `watch` against whichever shim is on `window` right now, re-arming from
   * scratch if that is not the one the attempt in progress was bound to.
   *
   * Returns true once the bound shim has completed both halves of the handshake.
   */
  private advanceHandshake(watch: BridgeWatch): boolean {
    const shim =
      typeof window === "undefined" ? undefined : window.bifrostNativeBridge;

    if (shim !== watch.generation) {
      watch.generation = shim;
      watch.sent = false;
      watch.acked = false;
      // A fresh send needs a fresh id, so acks still owed to the previous shim
      // cannot answer it.
      watch.handshakeId = NativeHandshake.nextHandshakeId();
    }
    if (!shim) {
      // BEGIN stforestkit#469 compat — delete this block with `src/compat` (see its README).
      // The shim is missing, which on an affected host may mean its injection was
      // permanently skipped by a stale once-guard mark. Self-limiting: a no-op unless
      // that exact state is present.
      attemptStForestKitOnceGuardRecovery();
      // END stforestkit#469 compat
      return false;
    }

    if (!watch.sent) {
      if (typeof shim.onWebReady !== "function") {
        return false;
      }
      // Marked before the call, not after: the injected shim acks synchronously
      // from inside `onWebReady()`, so `signalNativeReady` re-enters here while the
      // call is still on the stack and would otherwise reject its own ack.
      watch.sent = true;
      watch.sending = true;
      try {
        // Hosts that echo the id back can be attributed exactly; the rest are
        // covered by the synchronous window this flag opens.
        shim.onWebReady(watch.handshakeId);
      } catch (error) {
        // A throw leaves the send unanswered, so roll the mark back and let the
        // next tick try the same shim again — otherwise a momentary host fault is
        // permanent unless the host happens to swap the shim out. An ack that
        // already landed inside the call still answers it, and survives.
        if (!watch.acked) {
          watch.sent = false;
          watch.handshakeId = NativeHandshake.nextHandshakeId();
          throw error;
        }
      } finally {
        watch.sending = false;
      }
    }

    const ready = watch.acked || Boolean(shim.isReady);

    // Both `onWebReady()` and the `isReady` getter are host code and either can swap
    // the shim mid-pass. One check after both have run covers either seam: readiness
    // reported by an outgoing generation must not settle the incoming one, which has
    // been sent nothing and has reset neither half of the handshake.
    if (window.bifrostNativeBridge !== shim) {
      return false;
    }

    return ready;
  }

  /**
   * Poll the handshake until it completes, this bridge is destroyed, or the watch
   * reaches its deadline.
   *
   * The poll has to keep going for the caller's whole window, not just until the
   * shim first appears: the host injects it on DOM-ready and re-injects on a
   * watchdog, so on a slow page it can land seconds in. A deadline shorter than the
   * host's own leaves the page unable to hand shake even though the bridge is
   * present on `window` — indistinguishable downstream from a healthy bridge that
   * never delivered anything. See `DEFAULT_READY_TIMEOUT_MS`.
   */
  private watchForNativeBridge(watch: BridgeWatch, settle: () => void): void {
    // Nothing more should be attempted once any of these holds. Read as a function
    // rather than captured, because `advanceHandshake` runs host code that can flip
    // all three from underneath us.
    const terminal = () =>
      this.host.isDestroyed() || watch.stopped || Date.now() > watch.deadline;

    // Exactly one future evaluation is ever queued, whether it was asked for by the
    // regular poll, by a drain that ran out of passes, or by an ack. Letting each
    // source keep its own timer meant a yielded drain and the poll both scheduled
    // successors, so every poll interval added another evaluation stream.
    let handle: ReturnType<typeof setTimeout> | undefined;
    let scheduledFor = Infinity;

    const schedule = (delayMs: number) => {
      const runAt = Date.now() + delayMs;
      if (handle !== undefined) {
        // Keeping the earliest is what stops a stream of poll-length reschedules
        // from pushing a yielded drain out indefinitely. Clearing before setting is
        // the part that keeps the count at one; no test separates the two, since
        // dropping this only lengthens a delay rather than adding a timer.
        if (runAt >= scheduledFor) {
          return;
        }
        clearTimeout(handle);
      }
      scheduledFor = runAt;
      handle = setTimeout(() => {
        handle = undefined;
        scheduledFor = Infinity;
        run();
      }, delayMs);
    };

    /** One pass over the handshake, draining reruns requested from host code. */
    const evaluate = (): "ready" | "pending" | "yielded" => {
      if (watch.evaluating) {
        // Re-entered from host code. Ask for another pass instead of nesting: the
        // loop below drains the request once the current pass unwinds, so depth
        // stays at one however long a chain of self-replacing shims gets.
        watch.rerunRequested = true;
        return "pending";
      }
      watch.evaluating = true;
      try {
        for (
          let pass = 0;
          pass < NativeHandshake.MAX_EVALUATION_PASSES;
          pass++
        ) {
          watch.rerunRequested = false;
          if (terminal()) {
            return "pending";
          }
          let ready = false;
          try {
            ready = this.advanceHandshake(watch);
          } catch {
            // `onWebReady` and the `isReady` getter are host code. A throwing shim
            // is just a not-ready one; letting it escape would kill the poll loop
            // while leaving the watch un-stopped in the shared memo, so every later
            // caller inherits a handshake that can never complete.
            ready = false;
          }
          // Re-checked after the fact: that same host code can destroy the bridge
          // or stop this watch from inside the call, and a handshake completed
          // against a bridge that no longer exists must not resolve anybody's
          // `initialize()`.
          if (terminal()) {
            return "pending";
          }
          if (ready) {
            settle();
            return "ready";
          }
          if (!watch.rerunRequested) {
            return "pending";
          }
        }
        // The host is asking for passes without ever completing. Yield rather than
        // spinning here -- draining inline forever would block the page just as
        // surely as the recursion this loop replaced -- and let the caller queue the
        // continuation, so it does not become a second source of timers.
        return "yielded";
      } finally {
        watch.evaluating = false;
      }
    };

    const run = () => {
      const outcome = evaluate();
      if (outcome === "ready" || terminal()) {
        return;
      }
      schedule(
        outcome === "yielded" ? 0 : NativeHandshake.BRIDGE_POLL_INTERVAL_MS,
      );
    };

    // An ack evaluates now and reschedules through the same single handle, so it
    // brings the next evaluation earlier rather than adding one.
    watch.evaluate = run;
    run();
  }

  /**
   * The in-flight handshake, shared by every `initialize()` call that overlaps it,
   * so overlapping callers send one `onWebReady()` between them rather than one
   * each when the shim lands.
   *
   * A watch is reused only while it is still running and its deadline still covers
   * the new caller's whole timeout window — otherwise the caller could outlive the
   * watcher it is relying on and miss a shim that arrives inside its own timeout.
   * `stop()` on handshake completion is what drops the memo, so polling never
   * continues past a successful handshake.
   */
  private bridgeWatch(timeoutMs: number): BridgeWatch {
    const now = Date.now();
    const required = now + timeoutMs;
    const existing = this.pendingWatch;

    // A live watch is extended, never replaced. Replacing it would strand any
    // caller already awaiting its promises on promises that can now never settle,
    // so that caller would reject at its own timeout even if the replacement
    // completed the handshake first. Extending also keeps the deadline covering
    // every live caller's window, which is what stops a caller from outliving the
    // watch it depends on.
    if (existing && !existing.stopped && now <= existing.deadline) {
      existing.deadline = Math.max(existing.deadline, required);
      return existing;
    }
    existing?.stop();

    // `settled` is a real deferred from the outset, never a placeholder swapped out
    // afterwards: the first poll tick runs synchronously below, and host code
    // reached from it can re-enter `initialize()` and be handed this same watch out
    // of the memo. A half-built watch carrying an already-resolved stand-in would
    // report a completed handshake to that caller.
    let settle!: () => void;
    const settled = new Promise<void>((resolve) => {
      settle = resolve;
    });

    const watch: BridgeWatch = {
      deadline: required,
      stopped: false,
      sent: false,
      acked: false,
      sending: false,
      evaluating: false,
      rerunRequested: false,
      handshakeId: NativeHandshake.nextHandshakeId(),
      settled,
      stop: () => {
        watch.stopped = true;
        if (this.pendingWatch === watch) {
          this.pendingWatch = undefined;
        }
      },
      // An ack has to answer *this watch's current send*. Two ways to establish
      // that:
      //
      //  1. The host echoed back the id we passed to `onWebReady(handshakeId)`.
      //  2. The host sent the legacy *bare* `"native_ready"` while that call was
      //     still on the stack, so no other send could have produced it.
      //
      // The synchronous window is for the bare form only. An envelope carries an id
      // and is therefore always attributable, so one naming a different send is a
      // straggler however it arrives -- being delivered synchronously does not make
      // it answer the call it was not addressed to.
      //
      // An asynchronous bare ack is refused for the same reason: it cannot be told
      // apart from a replaced shim's straggler. Such hosts can still complete
      // through `shim.isReady`.
      signalNativeReady: (ack: NativeReadyAck) => {
        // No separate "have we sent yet" check: a bare ack is refused unless the send
        // is on the stack, and an envelope can only echo an id the host was given by
        // a send, so neither form can be credited before one.
        const answersThisSend = ack.bare
          ? watch.sending
          : ack.handshakeId === watch.handshakeId;
        if (!answersThisSend) {
          return;
        }
        watch.acked = true;
        // A tick may be up to a poll interval away and the caller's deadline can
        // fall inside that gap, so a valid ack is acted on now rather than merely
        // recorded. Safe to call unconditionally: `evaluate` serializes, so an ack
        // raised from inside host code queues a further pass instead of recursing.
        watch.evaluate?.();
      },
    };

    // Retiring the memo cannot depend on a caller observing the success: the
    // `initialize()` that owned the await may have already rejected on its own
    // timeout, leaving the watch to complete with nobody watching. A fulfilled
    // watch must not then be handed to a later caller as if it were current.
    void settled.then(() => {
      watch.stop();
      this.host.onCompleted();
    });

    // Published before polling starts, not after: the first tick runs synchronously
    // and the injected shim acks synchronously from inside `onWebReady()`, so
    // `handleGlobalMessage` re-enters while this is still being built. Assigning
    // afterwards would leave `pendingWatch` unset for exactly that ack.
    this.pendingWatch = watch;
    this.watchForNativeBridge(watch, settle);

    return watch;
  }
}
